/**
 * What the business earned.
 *
 * WHY IT LOOKS LIKE A STATEMENT AND NOT LIKE A DASHBOARD
 * ------------------------------------------------------
 * This is the page somebody prints and takes to an accountant, or reads down
 * on the last day of the month. It is meant to be followed line by line, which
 * is what an income statement has always been for — so it is set as one:
 * accounts named, figures in a column, subtotals where the arithmetic changes.
 *
 * The dashboard answers "how are we doing today" in three seconds. This
 * answers "where did the money go" in three minutes, and the two are different
 * jobs.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, today, dateKey } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ReportExport } from './ReportExport.jsx';
import { ReportTabs } from './ReportTabs.jsx';

/** The first of the month a date falls in. */
function startOfMonth(day) {
  return `${day.slice(0, 7)}-01`;
}

export default function ProfitLossPage() {
  const [from, setFrom] = useState(() => startOfMonth(today()));
  const [to, setTo] = useState(today);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'profit-loss', from, to],
    queryFn: () => api.get(withQuery('/reports/profit-loss', { start_date: from, end_date: to })),
  });

  function setMonthsAgo(months) {
    const start = new Date();
    start.setMonth(start.getMonth() - months, 1);

    const end = months === 0 ? new Date() : new Date(start.getFullYear(), start.getMonth() + 1, 0);
    setFrom(dateKey(start));
    setTo(dateKey(end));
  }

  return (
    <div className="mx-auto max-w-3xl p-4">
      <ReportTabs />
      <div className="no-print mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Profit and loss</h1>
          <p className="mt-0.5 text-sm text-ink-2">Taken from the books, so it agrees with them.</p>
          {/*
            Beside the title rather than at the foot: somebody who came here to
            send this to an accountant should not have to read the whole
            statement first to find out they can.
          */}
          <div className="mt-2.5">
            <ReportExport
              path={withQuery('/reports/profit-loss', { start_date: from, end_date: to })}
              name={`Profit and loss ${from} to ${to}`}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label>
            <span className="mb-1.5 block text-xs text-ink-3">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          </label>
          <label>
            <span className="mb-1.5 block text-xs text-ink-3">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          </label>
          <Button variant="secondary" onClick={() => setMonthsAgo(0)}>
            This month
          </Button>
          <Button variant="secondary" onClick={() => setMonthsAgo(1)}>
            Last month
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Working it out" />
        </div>
      ) : isError || !data ? (
        <p className="py-16 text-center text-sm text-ink-2">
          Could not work this out. Check the branch computer is running.
        </p>
      ) : (
        <>
          {data.coverage?.warning && (
            <p className="no-print mb-4 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
              {data.coverage.warning}
            </p>
          )}

          <div className="statement rounded-xl border border-rule bg-surface p-5">
            <header className="mb-4 border-b border-rule pb-3 text-center">
              <h2 className="font-semibold text-ink">Profit and loss</h2>
              <p className="text-sm text-ink-2">
                {data.period.startDate} to {data.period.endDate}
              </p>
            </header>

            <Section title="Revenue" lines={data.revenue.lines} total={data.revenue.total} />

            <Section
              title="Cost of sales"
              lines={data.costOfSales.lines}
              total={data.costOfSales.total}
            />

            <Subtotal
              label="Gross profit"
              value={data.grossProfit}
              note={`${data.grossMarginPercent}% of revenue`}
            />

            <Section title="Overheads" lines={data.overheads.lines} total={data.overheads.total} />

            <div
              className={`mt-4 flex items-baseline justify-between border-t-2 pt-3 ${
                data.netProfit < 0 ? 'border-bad' : 'border-brand-400'
              }`}
            >
              <span className="font-semibold text-ink">
                {data.netProfit < 0 ? 'Net loss' : 'Net profit'}
              </span>
              <span className="text-right">
                <span
                  className={`block text-2xl font-semibold tabular-nums ${
                    data.netProfit < 0 ? 'text-bad' : 'text-ink'
                  }`}
                >
                  {formatPKR(Math.abs(data.netProfit))}
                </span>
                <span className="block text-xs text-ink-3">
                  {data.netMarginPercent}% of revenue
                </span>
              </span>
            </div>
          </div>

          <div className="no-print mt-4 flex justify-end">
            <Button variant="secondary" onClick={() => window.print()}>
              Print
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, lines, total }) {
  return (
    <section className="mt-4 first:mt-0">
      <h3 className="text-sm font-medium text-ink-2">{title}</h3>

      {lines.length === 0 ? (
        <p className="py-2 text-sm text-ink-3">Nothing in this period.</p>
      ) : (
        <dl className="mt-1.5">
          {lines.map((line) => (
            <div key={line.code} className="flex items-baseline justify-between gap-3 py-0.5">
              <dt className="min-w-0 text-sm text-ink-2">
                {/* The account code, so a figure can be traced into the ledger
                    rather than taken on trust. */}
                <span className="mr-2 text-xs tabular-nums text-ink-3">{line.code}</span>
                {line.name}
              </dt>
              <dd className="shrink-0 text-sm tabular-nums text-ink">{formatPKR(line.amount)}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-1 flex items-baseline justify-between border-t border-rule pt-1.5">
        <span className="text-sm font-medium text-ink">Total {title.toLowerCase()}</span>
        <span className="font-medium tabular-nums text-ink">{formatPKR(total)}</span>
      </div>
    </section>
  );
}

function Subtotal({ label, value, note }) {
  return (
    <div className="mt-3 flex items-baseline justify-between rounded-lg bg-surface-3 px-3 py-2.5">
      <span className="font-medium text-ink">{label}</span>
      <span className="text-right">
        <span className="block font-semibold tabular-nums text-ink">{formatPKR(value)}</span>
        {note && <span className="block text-xs text-ink-3">{note}</span>}
      </span>
    </div>
  );
}
